import React, { useCallback, useEffect, useRef, useState } from "react";
import EventBus from "@/lib/eventBus";
import { Link, useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCurrentAccountId } from "@/store/authContext/authContext";
import { deleteItem, getReports } from "@/lib/serverComm";
import { useInView } from "react-intersection-observer";
import type { ReportForUI } from "@/types/report";

type ReportsProps = {
  onReportClick?: (reportId: string) => void;
};

const Reports: React.FC<ReportsProps> = ({ onReportClick }) => {
  const currentAccountId = useCurrentAccountId();
  const navigate = useNavigate();

  // Local state for reports
  const [reports, setReports] = useState<ReportForUI[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Parameters for API calls
  const [params, setParams] = useState({
    limit: 20,
    offset: 0,
    query: "",
  });

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: "100px",
  });

  if (!onReportClick) {
    onReportClick = (reportId: string) => {
      navigate(`/report/${reportId}`);
    };
  }

  // Function to load reports
  const loadReports = useCallback(
    async (resetData = false, searchQuery = params.query) => {
      if (loading) return;

      setLoading(true);
      setError(null);

      try {
        const offsetToUse = resetData ? 0 : params.offset;
        const newReports = await getReports(
          params.limit,
          searchQuery,
          offsetToUse,
          false,
          currentAccountId,
        );

        if (resetData) {
          setReports(newReports);
          setParams((prev) => ({
            ...prev,
            offset: newReports.length,
            query: searchQuery,
          }));
        } else {
          setReports((prev) => [...prev, ...newReports]);
          setParams((prev) => ({
            ...prev,
            offset: prev.offset + newReports.length,
          }));
        }

        // If we got fewer reports than requested, we've reached the end
        setHasMore(newReports.length === params.limit);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load reports");
      } finally {
        setLoading(false);
      }
    },
    [params.limit, params.query, params.offset, currentAccountId, loading],
  );

  const handleDeleteReport = useCallback(async (reportId: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this report? This action cannot be undone.",
    );
    if (!confirmed) return;

    try {
      await deleteItem("reports", reportId);
      setReports((prev) => prev.filter((report) => report.id !== reportId));
      EventBus.$emit("sidebar:refreshReports");
    } catch (err) {
      alert(
        err instanceof Error
          ? `Failed to delete report: ${err.message}`
          : "Failed to delete report",
      );
    }
  }, []);

  // Load more when scrolling into view
  useEffect(() => {
    if (inView && hasMore && !loading && reports.length > 0) {
      loadReports(false);
    }
  }, [inView, hasMore, loading, reports.length, loadReports]);

  // Initial load and reload when account or query changes
  useEffect(() => {
    setReports([]);
    setHasMore(true);

    const loadInitialReports = async () => {
      setLoading(true);
      setError(null);

      try {
        const newReports = await getReports(
          20, // limit
          "", // initial empty query
          0, // offset
          false,
          currentAccountId,
        );

        setReports(newReports);
        setParams({ limit: 20, offset: newReports.length, query: "" });
        setHasMore(newReports.length === 20);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load reports");
      } finally {
        setLoading(false);
      }
    };

    loadInitialReports();

    // Cleanup debounce timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [currentAccountId]);

  // Debounced search
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();

  const debouncedSearch = useCallback(
    (searchTerm: string) => {
      console.log("Debounced search term:", searchTerm);

      // Clear previous timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout
      debounceTimeoutRef.current = setTimeout(() => {
        setReports([]);
        setHasMore(true);
        loadReports(true, searchTerm);
      }, 300);
    },
    [loadReports],
  );

  return (
    <ScrollArea className="h-full">
      <div className="container mx-auto px-4 pt-4 text-gray-800 dark:text-gray-200 max-w-3xl h-full">
        <h1 className="text-3xl font-bold mb-6">Your Reports</h1>
        <input
          type="text"
          placeholder="Search reports..."
          className="w-full p-2 mb-6 border rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          onChange={(e) => debouncedSearch(e.target.value)}
        />
        {renderContent()}
      </div>
    </ScrollArea>
  );

  function renderContent() {
    if (loading && reports.length === 0) {
      return (
        <p className="text-gray-600 dark:text-gray-400">Loading reports...</p>
      );
    }
    if (error) return <p className="text-red-500 dark:text-red-400">{error}</p>;
    if (reports.length === 0) {
      return (
        <p className="text-gray-600 dark:text-gray-400">
          No results found matching your search.
        </p>
      );
    }
    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reports.map(renderReportLink)}
        </div>
        {hasMore && <div ref={ref} className="h-4 w-full my-4" />}
        {loading && reports.length > 0 && (
          <p className="text-center text-gray-600 dark:text-gray-400 py-4">
            Loading more reports...
          </p>
        )}
      </>
    );
  }

  function renderReportLink(report: ReportForUI) {
    return (
      <div
        key={report.id}
        className="relative p-4 border rounded bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-gray-200 dark:border-gray-700"
      >
        <Link
          to={`/report/${report.id}`}
          onClick={(e) => {
            e.preventDefault();
            onReportClick?.(report.id);
          }}
          className="block"
        >
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            {report.title}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Last updated: {new Date(report.updated_at).toLocaleString()}
          </p>
        </Link>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDeleteReport(report.id);
          }}
          className="absolute bottom-3 right-3 p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          aria-label="Delete report"
        >
          <Trash2 size={16} />
        </button>
      </div>
    );
  }
};

export default Reports;
